import { yupResolver } from '@hookform/resolvers/yup'
import { isArray } from 'lodash'
import React, {
  ChangeEvent,
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo
} from 'react'
import { Col, Row } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { array, object, string, StringSchema } from 'yup'

import { ClinTheme } from '../../../ClinTheme'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinSelect, ClinSelectVariant } from '../../../components/ClinSelect'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinText } from '../../../components/ClinText'
import { ClinTextInput } from '../../../components/ClinTextInput'
import {
  ContactTitles,
  PhoneContactResponseType,
  phoneTypesRecord,
  userTitleRecord,
  countryCodes,
  PhoneResponseType
} from '../../../constants'
import {
  ContactPhoneDto,
  LovDto,
  UpdateContactDto,
  InstituteContactDto
} from '../../../types/swaggerTypes'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../../utils/Forms/getInputPlaceholder'
import {
  getInputValidation,
  ValidationType
} from '../../../utils/Forms/getInputValidation'
import { requiredMinStringLength } from '../../../utils/Forms/requiredMinStringLength'
import {
  StyledLoadingContainer,
  StyledLoadingSpinner,
  StyledFormContainer,
  StyledRow,
  StyledColumn,
  StyledMobileColumn
} from './AccountDetailsEditUserForm.styles'
import {
  cancelGetContactDetails,
  cancelGetSpecialisms
} from '../../../services/ApiService'
import {
  StyledClinSecondaryButton,
  StyledClinPrimaryButton
} from '../AccountDetails/AccountDetails.styles'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'

interface IAccountDetailsEditUserFormProps {
  /** Contact to be edited */
  isLoading: boolean
  /** An array of specialism values */
  specialisms?: LovDto[]
  /** An array of lov job title values */
  jobTitles?: LovDto[]
  /** Contact to be edited */
  contact?: InstituteContactDto
  handleUserFormSubmission: (
    data: UpdateContactDto,
    contact: InstituteContactDto
  ) => void
  handleUserFormCancel: () => void
  isUserSubmitting: boolean
}

export const AccountDetailsEditUserForm: FunctionComponent<
  IAccountDetailsEditUserFormProps
> = ({
  isLoading,
  specialisms,
  jobTitles,
  contact,
  handleUserFormSubmission,
  handleUserFormCancel,
  isUserSubmitting
}) => {
  const { t } = useTranslation()

  const EditContactFormSchema = object().shape({
    firstName: requiredMinStringLength(100),
    lastName: requiredMinStringLength(100),
    title: requiredMinStringLength(100),
    preferredName: string().test(
      'len',
      getInputValidation(ValidationType.MaxiumumCharacters, '240'),
      (val) => {
        return val === '' || (val && val.length < 240) ? true : false
      }
    ),
    licenseNumber: string().when(
      'classification',
      (classification: string, schema: StringSchema) => {
        const c = (classification || '').toLowerCase()
        const mustHave = c === 'pharmacist' || c === 'physician'
        return mustHave
          ? schema.required(getInputValidation(ValidationType.RequiredField))
          : schema.notRequired()
      }
    ),
    specialism: requiredMinStringLength(240),
    classification: requiredMinStringLength(240).nullable(),
    phones: array()
      .compact()
      .of(
        object().shape(
          {
            phoneId: string(),
            requestType: string(),
            phoneType: string(),
            areaCode: string().matches(
              /^\d*$/,
              t('account_details:form.only_digits_allowed')
            ),
            phoneNumber: string()
              .matches(/^\d*$/, t('account_details:form.only_digits_allowed'))
              .notRequired(),
            phoneExtension: string()
              .nullable()
              .when('requestType', {
                is: (val: string) => val === PhoneContactResponseType.CREATE,
                then: (schema) =>
                  schema
                    .matches(
                      /^\d*$/,
                      t('account_details:form.only_digits_allowed')
                    )
                    .required(getInputValidation(ValidationType.RequiredField)),
                otherwise: (schema) =>
                  schema
                    .transform(() => null)
                    .notRequired()
                    .nullable()
              })
          },
          [['areaCode', 'phoneNumber']]
        )
      )
  })

  const getPhoneNumberKey = (item: ContactPhoneDto) => {
    if (item.phoneType === PhoneResponseType.MOBILE) {
      return 'edit_contact:mobile_number'
    } else if (item.phoneType === PhoneResponseType.FAX) {
      return 'edit_contact:fax_number'
    } else {
      return 'edit_contact:phone_number'
    }
  }

  const contactTitlesOptions = Object.values(ContactTitles)
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    control
  } = useForm<UpdateContactDto>({
    defaultValues: contact,
    resolver: yupResolver(EditContactFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const watchClassification = watch('classification')
  const watchedPhones = watch('phones')
  const showLicenceNumber =
    watchClassification &&
    (watchClassification.toLowerCase() === 'pharmacist' ||
      watchClassification.toLowerCase() === 'physician')

  const hasAtLeastOnePhone = useMemo(() => {
    if (
      !watchedPhones ||
      !Array.isArray(watchedPhones) ||
      watchedPhones.length === 0
    ) {
      return false
    }

    return watchedPhones.some((phone) => {
      if (!phone || typeof phone !== 'object') return false

      const phoneNumber = phone.phoneNumber
      return (
        phoneNumber != null &&
        phoneNumber !== '' &&
        String(phoneNumber).trim().length > 0
      )
    })
  }, [watchedPhones])

  useEffect(() => {
    if (!hasAtLeastOnePhone) {
      setError('phones', {
        type: 'manual',
        message: 'At least one phone number is required for this user'
      })
    } else {
      clearErrors('phones')
    }
  }, [hasAtLeastOnePhone, watchedPhones, setError, clearErrors])

  const shouldDisableSaveButton = (): boolean => {
    if (
      !watchedPhones ||
      !Array.isArray(watchedPhones) ||
      watchedPhones.length === 0
    ) {
      return true
    }

    const allPhonesEmpty = watchedPhones.every((phone) => {
      const phoneNumber = phone?.phoneNumber
      return !phoneNumber || phoneNumber.trim() === ''
    })

    if (allPhonesEmpty) {
      return true
    }

    if (errors.phones && !Array.isArray(errors.phones)) {
      return true
    }

    const phoneErrors = errors.phones
    if (Array.isArray(phoneErrors)) {
      const hasPhoneErrors = phoneErrors.some(
        (phoneError) => phoneError?.phoneNumber
      )
      if (hasPhoneErrors) return true
    }

    return false
  }

  // Helper function to prepare phone data for submission
  const preparePhoneData = (phone: any) => ({
    ...phone,
    phoneId: phone?.phoneId?.toString()
  })

  // Helper function to prepare form data for submission
  const prepareFormData = (values: UpdateContactDto): UpdateContactDto => ({
    ...values,
    phones: values.phones.map(preparePhoneData)
  })

  const handlePhoneChange = (value: string, index: number) => {
    const path = `phones.${index}.phoneNumber` as const

    const isRemovingLastPhone =
      value === '' &&
      watchedPhones.filter((p, i) => i !== index && p?.phoneNumber?.trim())
        .length === 0

    if (isRemovingLastPhone) {
      setError('phones', {
        type: 'manual',
        message: 'At least one phone number is required for this user'
      })
    } else if (value && !/^\d+$/.test(value)) {
      setError(path, {
        type: 'manual',
        message: t('account_details:form.only_digits_allowed')
      })
    } else {
      clearErrors(path)
      const hasEnoughPhones = watchedPhones.some((p, i) =>
        (i === index ? value : p?.phoneNumber)?.trim()
      )

      if (hasEnoughPhones) {
        clearErrors('phones')
      }
    }
  }

  useEffect(() => {
    reset()
  }, [contact, reset])

  useEffect(() => {
    return () => {
      cancelGetContactDetails()
      cancelGetSpecialisms()
    }
  }, [])

  return (
    <>
      {isLoading && (
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      )}

      {!isLoading && contact && specialisms && jobTitles && (
        <StyledFormContainer>
          <form
            onSubmit={handleSubmit((values) => {
              const updatedValues = prepareFormData(values)
              handleUserFormSubmission(updatedValues, contact)
            })}
          >
            <Row>
              <Col xs={12}>
                <ClinText
                  fontSize={ClinTheme.fontSizes[2]}
                  lineHeight={ClinTheme.lineHeights.body}
                  marginTop={0}
                  marginBottom={0}
                >
                  {t('edit_contact:description')}
                </ClinText>
                <ClinSpacer />
                <ClinText
                  fontSize={ClinTheme.fontSizes[2]}
                  lineHeight={ClinTheme.lineHeights.body}
                  marginTop={0}
                  marginBottom={0}
                >
                  {t('edit_contact:mandatory_description')}
                </ClinText>
              </Col>
            </Row>
            <ClinSpacer height={ClinTheme.space[3]} />
            <StyledRow>
              <StyledColumn width={160}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <ClinSelect
                      id="title"
                      label={t('edit_contact:title_label')}
                      width="100%"
                      defaultValue={contact?.title}
                      variant={ClinSelectVariant.primaryV2}
                      onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                        onChange(changeValue.currentTarget.value)
                      }
                      hasError={!!(errors && errors.title)}
                      prompt={errors.title?.message}
                    >
                      <option value="" disabled={true}>
                        {getInputPlaceholder(PlaceholderType.SelectInput)}
                      </option>
                      {contactTitlesOptions.map((title: string, index) => {
                        return (
                          <option key={`title-${index}`} value={title}>
                            {t(
                              `${
                                userTitleRecord[title as ContactTitles].transKey
                              }`
                            )}
                          </option>
                        )
                      })}
                    </ClinSelect>
                  )}
                />
              </StyledColumn>
              <StyledColumn width={198}>
                <ClinTextInput
                  id="first-name"
                  {...register('firstName')}
                  label={t('edit_contact:first_name')}
                  width="100%"
                  labelFontWeight={ClinTheme.fontWeights.normal}
                  hasError={!!(errors && errors.firstName)}
                  prompt={errors.firstName?.message}
                />
              </StyledColumn>
              <StyledColumn width={198}>
                <ClinTextInput
                  id="last-name"
                  {...register('lastName')}
                  label={t('edit_contact:last_name')}
                  width="100%"
                  labelFontWeight={ClinTheme.fontWeights.normal}
                  hasError={!!(errors && errors.lastName)}
                  prompt={errors.lastName?.message}
                />
              </StyledColumn>
            </StyledRow>

            <ClinSpacer height={ClinTheme.space[3]} />

            <StyledRow>
              <StyledColumn width={286}>
                <ClinTextInput
                  id="preferred-name"
                  {...register('preferredName')}
                  label={t('edit_contact:preferred_name')}
                  width="100%"
                  labelFontWeight={ClinTheme.fontWeights.normal}
                  hasError={!!(errors && errors.preferredName)}
                  prompt={errors.preferredName?.message}
                />
                <ClinSpacer height={ClinTheme.space[3]} />
              </StyledColumn>
            </StyledRow>

            <StyledRow>
              {specialisms && (
                <StyledColumn width={286}>
                  <Controller
                    name="specialism"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <ClinSelect
                        id="specialism"
                        label={t('edit_contact:specialism')}
                        width="100%"
                        value={value}
                        variant={ClinSelectVariant.primaryV2}
                        onChange={(
                          changeValue: ChangeEvent<HTMLSelectElement>
                        ) => onChange(changeValue.currentTarget.value)}
                        hasError={!!(errors && errors.specialism)}
                        prompt={errors.specialism?.message}
                      >
                        <option value="" disabled={true}>
                          {getInputPlaceholder(PlaceholderType.SelectInput)}
                        </option>
                        {specialisms
                          .sort((a, b) =>
                            a.lovValue && b.lovValue
                              ? a.lovValue.localeCompare(b.lovValue)
                              : 0
                          )
                          .map((specialism: LovDto, index) => {
                            return (
                              <option
                                key={`specialism${index}`}
                                value={specialism.lovCode}
                              >
                                {specialism.lovValue}
                              </option>
                            )
                          })}
                      </ClinSelect>
                    )}
                  />
                </StyledColumn>
              )}

              <StyledColumn width={286}>
                <Controller
                  name="classification"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <ClinSelect
                      id="classification"
                      label={t('edit_contact:job_type')}
                      width="100%"
                      value={value}
                      variant={ClinSelectVariant.primaryV2}
                      onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                        onChange(changeValue.currentTarget.value)
                      }
                      hasError={!!(errors && errors.classification)}
                      prompt={errors.classification?.message}
                    >
                      <option value="" disabled={true}>
                        {getInputPlaceholder(PlaceholderType.SelectInput)}
                      </option>
                      {jobTitles.map((jobType: LovDto, index) => {
                        return (
                          <option
                            key={`classification-${index}`}
                            value={jobType.lovCode}
                          >
                            {jobType.lovValue}
                          </option>
                        )
                      })}
                    </ClinSelect>
                  )}
                />
              </StyledColumn>
            </StyledRow>

            <ClinSpacer height={ClinTheme.space[3]} />

            <StyledRow
              style={{ display: showLicenceNumber ? 'block' : 'none' }}
            >
              <StyledColumn>
                <ClinTextInput
                  {...register('licenseNumber')}
                  id="license-number"
                  label={t('edit_contact:license_number')}
                  width="100%"
                  labelFontWeight={ClinTheme.fontWeights.normal}
                  hasError={!!(errors && errors.licenseNumber)}
                  prompt={errors.licenseNumber?.message}
                />
                <ClinSpacer height={ClinTheme.space[4]} />
              </StyledColumn>
            </StyledRow>

            {contact?.phones.length !== 0 &&
              contact?.phones.map((item: ContactPhoneDto, index) => {
                const phoneErrorValues =
                  isArray(errors.phones) &&
                  errors.phones[index] !== undefined &&
                  errors.phones[index]

                const responseType =
                  watch(`phones.${index}.countryCode`) === '' &&
                  watch(`phones.${index}.areaCode`) === '' &&
                  watch(`phones.${index}.phoneNumber`) === ''
                    ? PhoneContactResponseType.DELETE
                    : PhoneContactResponseType.UPDATE

                return (
                  <Fragment key={`phone-type-${index}`}>
                    <StyledRow>
                      <StyledMobileColumn width={286}>
                        <StyledColumn width={135}>
                          <div style={{ display: 'none' }}>
                            <ClinTextInput
                              {...register(`phones.${index}.phoneId` as const)}
                              id="phone-id"
                            />
                            <ClinTextInput
                              id="phone-type"
                              {...register(
                                `phones.${index}.phoneType` as const
                              )}
                              value={item.phoneType}
                            />
                            <ClinTextInput
                              id="request-type"
                              {...register(
                                `phones.${index}.requestType` as const
                              )}
                              value={responseType}
                            />
                          </div>

                          <Controller
                            name={`phones.${index}.countryCode`}
                            control={control}
                            render={({ field: { onChange } }) => (
                              <ClinSelect
                                id="country-code"
                                label={t('edit_contact:country_code')}
                                width="100%"
                                defaultValue={item.countryCode}
                                variant={ClinSelectVariant.primaryV2}
                                onChange={(
                                  changeValue: ChangeEvent<HTMLSelectElement>
                                ) => onChange(changeValue.currentTarget.value)}
                                hasError={
                                  !!(
                                    phoneErrorValues &&
                                    phoneErrorValues.countryCode
                                  )
                                }
                                prompt={
                                  phoneErrorValues &&
                                  phoneErrorValues.countryCode
                                    ? phoneErrorValues.countryCode?.message
                                    : undefined
                                }
                              >
                                <option value="44">+44</option>
                                {countryCodes.map(
                                  (code: string | undefined, index: number) => {
                                    return (
                                      <option
                                        key={`code-${index}`}
                                        value={code}
                                      >
                                        {`+${code}`}
                                      </option>
                                    )
                                  }
                                )}
                              </ClinSelect>
                            )}
                          />
                        </StyledColumn>
                        <StyledColumn width={135}>
                          <ClinTextInput
                            id="area-code"
                            {...register(`phones.${index}.areaCode`)}
                            label={t('edit_contact:area_code')}
                            type="tel"
                            width="100%"
                            labelFontWeight={ClinTheme.fontWeights.normal}
                            hasError={
                              !!(phoneErrorValues && phoneErrorValues.areaCode)
                            }
                            prompt={
                              phoneErrorValues && phoneErrorValues.areaCode
                                ? phoneErrorValues.areaCode?.message
                                : undefined
                            }
                          />
                        </StyledColumn>
                      </StyledMobileColumn>
                      <StyledColumn width={286}>
                        <ClinTextInput
                          id="phone-number"
                          {...register(`phones.${index}.phoneNumber`)}
                          label={`${t(getPhoneNumberKey(item))}*`}
                          type="tel"
                          width="100%"
                          labelFontWeight={ClinTheme.fontWeights.normal}
                          hasError={
                            !!(phoneErrorValues && phoneErrorValues.phoneNumber)
                          }
                          prompt={
                            phoneErrorValues && phoneErrorValues.phoneNumber
                              ? phoneErrorValues.phoneNumber?.message
                              : undefined
                          }
                          onChange={(e) => {
                            handlePhoneChange(e.target.value, index)
                          }}
                          onFocus={(e) => {
                            handlePhoneChange(e.target.value, index)
                          }}
                        />
                      </StyledColumn>
                    </StyledRow>
                    <ClinSpacer height={ClinTheme.space[4]} />
                  </Fragment>
                )
              })}

            {contact?.phones.length === 0 && (
              <>
                <StyledRow>
                  <StyledMobileColumn width={286}>
                    <StyledColumn width={135}>
                      <div style={{ display: 'none' }}>
                        <ClinTextInput
                          id="phone-type"
                          {...register(`phones.0.phoneType` as const)}
                          value={phoneTypesRecord['GEN'].type}
                        />
                        <ClinTextInput
                          id="request-type"
                          {...register(`phones.0.requestType` as const)}
                          value={PhoneContactResponseType.CREATE}
                        />
                      </div>

                      <Controller
                        name="phones.0.countryCode"
                        control={control}
                        defaultValue={'44'}
                        render={({ field: { onChange, value } }) => {
                          !value &&
                            setValue('phones.0.countryCode', '44', {
                              shouldDirty: true
                            })
                          return (
                            <ClinSelect
                              id="country-code"
                              label={t('edit_contact:country_code')}
                              width="100%"
                              defaultValue={value}
                              variant={ClinSelectVariant.primaryV2}
                              onChange={(
                                changeValue: ChangeEvent<HTMLSelectElement>
                              ) => onChange(changeValue.currentTarget.value)}
                              hasError={
                                !!(
                                  isArray(errors.phones) &&
                                  errors.phones[0] !== undefined &&
                                  errors.phones[0].countryCode
                                )
                              }
                              prompt={
                                isArray(errors.phones) &&
                                errors.phones[0] !== undefined &&
                                errors.phones[0].countryCode
                                  ? errors.phones[0].countryCode?.message
                                  : undefined
                              }
                            >
                              <option value="44">+44</option>
                              {countryCodes.map(
                                (code: string | undefined, index: number) => {
                                  return (
                                    <option key={`code-${index}`} value={code}>
                                      {`+${code}`}
                                    </option>
                                  )
                                }
                              )}
                            </ClinSelect>
                          )
                        }}
                      />
                    </StyledColumn>
                    <StyledColumn width={135}>
                      <ClinTextInput
                        id="area-code"
                        {...register('phones.0.areaCode')}
                        label={t('edit_contact:area_code')}
                        width="100%"
                        type="tel"
                        labelFontWeight={ClinTheme.fontWeights.normal}
                        hasError={
                          !!(
                            isArray(errors.phones) &&
                            errors.phones[0] !== undefined &&
                            errors.phones[0].areaCode
                          )
                        }
                        prompt={
                          isArray(errors.phones) &&
                          errors.phones[0] !== undefined &&
                          errors.phones[0].areaCode
                            ? errors.phones[0].areaCode?.message
                            : undefined
                        }
                      />
                    </StyledColumn>
                  </StyledMobileColumn>
                  <StyledColumn width={286}>
                    <ClinTextInput
                      id="phone-number"
                      {...register('phones.0.phoneNumber')}
                      label={`${t('edit_contact:phone_number')}*`}
                      width="100%"
                      type="tel"
                      labelFontWeight={ClinTheme.fontWeights.normal}
                      hasError={
                        !!(
                          errors.phones?.[0]?.phoneNumber ||
                          (!!errors.phones && !Array.isArray(errors.phones))
                        )
                      }
                      prompt={
                        errors.phones?.[0]?.phoneNumber?.message ||
                        (!Array.isArray(errors.phones)
                          ? errors.phones?.message
                          : undefined)
                      }
                      onChange={(e) => {
                        handlePhoneChange(e.target.value, 0)
                      }}
                      onFocus={(e) => {
                        handlePhoneChange(e.target.value, 0)
                      }}
                    />
                  </StyledColumn>
                </StyledRow>
                <ClinSpacer height={ClinTheme.space[4]} />
                <StyledRow>
                  <StyledColumn width={286}>
                    <ClinTextInput
                      id="phone-extension"
                      {...register('phones.0.phoneExtension')}
                      label={`${t('edit_contact:phone_extension')}*`}
                      width="100%"
                      type="tel"
                      labelFontWeight={ClinTheme.fontWeights.normal}
                      hasError={
                        !!(
                          isArray(errors.phones) &&
                          errors.phones[0] !== undefined &&
                          errors.phones[0].phoneExtension
                        )
                      }
                      prompt={
                        isArray(errors.phones) &&
                        errors.phones[0] !== undefined &&
                        errors.phones[0].phoneExtension
                          ? errors.phones[0].phoneExtension?.message
                          : undefined
                      }
                    />
                  </StyledColumn>
                  <ClinSpacer height={ClinTheme.space[4]} />
                </StyledRow>
              </>
            )}
            <ClinSpacer height={ClinTheme.space[1]} />

            <ClinGroup>
              <StyledClinSecondaryButton onClick={() => handleUserFormCancel()}>
                {t('common:buttons.cancel')}
              </StyledClinSecondaryButton>
              <StyledLoadingContainer>
                <StyledClinPrimaryButton
                  type="submit"
                  variant="primary"
                  disabled={shouldDisableSaveButton() || isUserSubmitting}
                >
                  {t('common:buttons.save')}
                </StyledClinPrimaryButton>
                {isUserSubmitting && (
                  <StyledLoadingSpinner>
                    <ClinSpinner />
                  </StyledLoadingSpinner>
                )}
              </StyledLoadingContainer>
            </ClinGroup>
          </form>
        </StyledFormContainer>
      )}
    </>
  )
}
